/**
 * RiskMatrix.js
 * Risk scoring and prioritization linked to vulnerability management.
 * Risk Score = CVSS × Exploitability × Business Impact (0-100 scale)
 */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

function getHeaders() {
  return { Authorization: `Bearer ${localStorage.getItem('adminToken')}` };
}

const SEV_COLOR = { critical: '#dc2626', high: '#ea580c', medium: '#d97706', low: '#16a34a' };
const SEV_BG    = { critical: '#fee2e2', high: '#ffedd5', medium: '#fef3c7', low: '#dcfce7' };

function RiskBar({ score }) {
  const pct   = Math.min(100, Math.max(0, score));
  const color = pct >= 70 ? '#dc2626' : pct >= 40 ? '#ea580c' : pct >= 20 ? '#d97706' : '#16a34a';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <div style={{ flex: 1, background: '#f0f4ff', borderRadius: 4, height: 8, overflow: 'hidden' }}>
        <div style={{ width: `${pct}%`, height: '100%', background: color, borderRadius: 4, transition: 'width 0.4s' }} />
      </div>
      <span style={{ fontSize: 12, fontWeight: 700, color, minWidth: 28 }}>{Math.round(pct)}</span>
    </div>
  );
}

function RiskBadge({ score }) {
  const label = score >= 70 ? 'Critical' : score >= 40 ? 'High' : score >= 20 ? 'Medium' : 'Low';
  const color = score >= 70 ? '#dc2626' : score >= 40 ? '#ea580c' : score >= 20 ? '#d97706' : '#16a34a';
  const bg    = score >= 70 ? '#fee2e2' : score >= 40 ? '#ffedd5' : score >= 20 ? '#fef3c7' : '#dcfce7';
  return (
    <span style={{ background: bg, color, padding: '2px 10px', borderRadius: 20,
      fontSize: 11, fontWeight: 700 }}>
      {label} ({Math.round(score)})
    </span>
  );
}

function fmtDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

export default function RiskMatrix({ user }) {
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab,     setTab]     = useState('matrix');   // matrix | devices | edit
  const [search,  setSearch]  = useState('');
  const [filter,  setFilter]  = useState('all');      // all | critical | high | medium | low
  const [editVuln, setEditVuln] = useState(null);
  const [editForm, setEditForm] = useState({ cvssScore: '', exploitability: 'unknown', businessImpact: 'medium' });
  const [saving,  setSaving]  = useState(false);
  const [msg,     setMsg]     = useState({ text: '', type: '' });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get('/api/compliance/risk-matrix', { headers: getHeaders() });
      setData(res.data);
    } catch (e) {
      flash('Failed to load risk matrix: ' + e.message, 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  function flash(text, type = 'success') {
    setMsg({ text, type });
    setTimeout(() => setMsg({ text: '', type: '' }), 5000);
  }

  function openEdit(v) {
    setEditVuln(v);
    setEditForm({
      cvssScore:      v.cvss_score || '',
      exploitability: v.exploitability || 'unknown',
      businessImpact: v.business_impact || 'medium'
    });
  }

  async function saveRisk() {
    if (!editVuln) return;
    setSaving(true);
    try {
      await axios.patch(`/api/compliance/vulnerabilities/${editVuln.id}/risk`, {
        cvssScore:      parseFloat(editForm.cvssScore) || null,
        exploitability: editForm.exploitability,
        businessImpact: editForm.businessImpact
      }, { headers: getHeaders() });
      flash(`Risk score updated for ${editVuln.title}`);
      setEditVuln(null);
      await load();
    } catch (e) {
      flash(e.response?.data?.error || e.message, 'error');
    } finally {
      setSaving(false);
    }
  }

  const vulns = data?.vulnerabilities || [];
  const filtered = vulns.filter(v => {
    const matchFilter = filter === 'all' || v.severity === filter;
    const matchSearch = !search || [v.title, v.hostname, v.affectedProduct, v.cveId]
      .join(' ').toLowerCase().includes(search.toLowerCase());
    return matchFilter && matchSearch;
  });

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">⚠ Risk Matrix</div>
          <div className="page-subtitle">
            Risk scoring and prioritization linked to vulnerability management
          </div>
        </div>
        <button className="refresh-btn" onClick={load} disabled={loading}>↻ Refresh</button>
      </div>

      {msg.text && (
        <div className={`alert ${msg.type === 'error' ? 'error' : 'success'}`} style={{ marginBottom: 16 }}>
          {msg.text}
        </div>
      )}

      {/* KPI cards */}
      {data && (
        <div className="cards-row" style={{ gridTemplateColumns: 'repeat(5,1fr)', marginBottom: 20 }}>
          {[
            { label: 'Total Vulns',  value: data.summary.total,    cls: 'blue',   filterVal: 'all'      },
            { label: 'Critical',     value: data.summary.critical, cls: 'red',    filterVal: 'critical' },
            { label: 'High',         value: data.summary.high,     cls: 'amber',  filterVal: 'high'     },
            { label: 'Medium',       value: data.summary.medium,   cls: 'cyan',   filterVal: 'medium'   },
            { label: 'Low',          value: data.summary.low,      cls: 'green',  filterVal: 'low'      },
          ].map(c => (
            <div key={c.label} className={`card ${c.cls}`}
              style={{ cursor:'pointer', outline: filter===c.filterVal && c.filterVal!=='all' ? '2px solid currentColor' : undefined }}
              onClick={() => { setFilter(filter===c.filterVal?'all':c.filterVal); setTab('matrix'); }}>
              <div className="card-label">{c.label}</div>
              <div className="card-value">{c.value}</div>
              <div style={{ fontSize:10, color:'inherit', opacity:0.7, marginTop:2 }}>
                {filter===c.filterVal ? '▼ filtered' : 'click to filter'}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Tabs */}
      <div style={{ display: 'flex', borderBottom: '2px solid #e8ecf4', marginBottom: 20 }}>
        {[
          { id: 'matrix',  label: '⚠ Risk Matrix' },
          { id: 'devices', label: '🖥 Top Risk Devices' },
        ].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{
            background: 'none', border: 'none', cursor: 'pointer',
            padding: '10px 20px', fontSize: 13, fontWeight: 600,
            color: tab === t.id ? '#dc2626' : '#94a3b8',
            borderBottom: tab === t.id ? '2px solid #dc2626' : '2px solid transparent',
            marginBottom: -2
          }}>
            {t.label}
          </button>
        ))}
      </div>

      {/* ── RISK MATRIX TAB ── */}
      {tab === 'matrix' && (
        <>
          {/* Filters */}
          <div style={{ display: 'flex', gap: 8, marginBottom: 16, alignItems: 'center', flexWrap: 'wrap' }}>
            {['all','critical','high','medium','low'].map(f => (
              <button key={f}
                className={`btn btn-sm ${filter === f ? 'btn-primary' : 'btn-outline'}`}
                onClick={() => setFilter(f)}
                style={filter === f && f !== 'all' ? { background: SEV_COLOR[f], borderColor: SEV_COLOR[f] } : {}}>
                {f === 'all' ? 'All' : f.charAt(0).toUpperCase() + f.slice(1)}
              </button>
            ))}
            <input className="form-input" style={{ maxWidth: 260, marginLeft: 'auto' }}
              placeholder="Search CVE / product / device..."
              value={search} onChange={e => setSearch(e.target.value)} />
          </div>

          <div className="panel">
            <div className="panel-header">
              <div className="panel-title">
                <div className="panel-title-icon">⚠</div>
                Vulnerabilities — sorted by Risk Score (highest first)
              </div>
              <span style={{ fontSize: 11, color: '#94a3b8' }}>
                {filtered.length} vulnerabilities
              </span>
            </div>

            {loading ? <div className="loading">Loading risk data...</div> : (
              <table>
                <thead>
                  <tr>
                    <th>CVE ID</th>
                    <th>Title</th>
                    <th>Device</th>
                    <th>Product</th>
                    <th>Severity</th>
                    <th>CVSS</th>
                    <th>Exploitability</th>
                    <th>Business Impact</th>
                    <th style={{ minWidth: 140 }}>Risk Score</th>
                    <th>Priority</th>
                    <th>Detected</th>
                    {user?.role === 'admin' && <th>Actions</th>}
                  </tr>
                </thead>
                <tbody>
                  {filtered.length === 0 && (
                    <tr><td colSpan={12} className="empty">No vulnerabilities found.</td></tr>
                  )}
                  {filtered.map((v, i) => (
                    <tr key={i}>
                      <td>
                        <code style={{ fontSize: 11, color: '#1a56db' }}>{v.cveId || '—'}</code>
                      </td>
                      <td style={{ maxWidth: 200, fontSize: 12 }}>{v.title}</td>
                      <td><strong style={{ fontSize: 12 }}>{v.hostname}</strong></td>
                      <td style={{ fontSize: 12, color: '#94a3b8' }}>{v.affectedProduct}</td>
                      <td>
                        <span className="badge" style={{ background: SEV_BG[v.severity], color: SEV_COLOR[v.severity] }}>
                          {v.severity}
                        </span>
                      </td>
                      <td style={{ fontSize: 12, fontWeight: 600 }}>
                        {v.cvss_score ? v.cvss_score.toFixed(1) : '—'}
                      </td>
                      <td>
                        <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 10,
                          background: v.exploitability === 'high' ? '#fee2e2' : v.exploitability === 'medium' ? '#fef3c7' : '#f1f5f9',
                          color: v.exploitability === 'high' ? '#dc2626' : v.exploitability === 'medium' ? '#d97706' : '#64748b' }}>
                          {v.exploitability || 'unknown'}
                        </span>
                      </td>
                      <td>
                        <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 10,
                          background: '#f1f5f9', color: '#64748b' }}>
                          {v.business_impact || 'medium'}
                        </span>
                      </td>
                      <td><RiskBar score={v.risk_score || 0} /></td>
                      <td><RiskBadge score={v.risk_score || 0} /></td>
                      <td style={{ fontSize: 11, color: '#94a3b8' }}>{fmtDate(v.detectedAt)}</td>
                      {user?.role === 'admin' && (
                        <td>
                          <button className="btn btn-sm btn-outline" onClick={() => openEdit(v)}>
                            ✏ Score
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Risk scoring legend */}
          <div className="panel" style={{ padding: 20 }}>
            <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 12, color: '#1e2a3b' }}>
              Risk Score Formula
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px,1fr))', gap: 12 }}>
              {[
                { label: 'Critical Risk', range: '70–100', color: '#dc2626', bg: '#fee2e2', desc: 'Patch immediately' },
                { label: 'High Risk',     range: '40–69',  color: '#ea580c', bg: '#ffedd5', desc: 'Patch within 7 days' },
                { label: 'Medium Risk',   range: '20–39',  color: '#d97706', bg: '#fef3c7', desc: 'Patch within 30 days' },
                { label: 'Low Risk',      range: '0–19',   color: '#16a34a', bg: '#dcfce7', desc: 'Patch in next cycle' },
              ].map(r => (
                <div key={r.label} style={{ background: r.bg, borderRadius: 8, padding: 12,
                  border: `1px solid ${r.bg}` }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: r.color }}>{r.label}</div>
                  <div style={{ fontSize: 20, fontWeight: 700, color: r.color, margin: '4px 0' }}>{r.range}</div>
                  <div style={{ fontSize: 11, color: '#64748b' }}>{r.desc}</div>
                </div>
              ))}
            </div>
            <div style={{ marginTop: 12, fontSize: 11, color: '#94a3b8' }}>
              Risk Score = Base Score × Exploitability Factor × Business Impact Factor (0–100 scale).
              Click ✏ Score on any vulnerability to set CVSS score, exploitability, and business impact.
            </div>
          </div>
        </>
      )}

      {/* ── TOP RISK DEVICES TAB ── */}
      {tab === 'devices' && (
        <div className="panel">
          <div className="panel-header">
            <div className="panel-title">
              <div className="panel-title-icon">🖥</div>
              Top Risk Devices — by cumulative risk score
            </div>
          </div>
          {loading ? <div className="loading">Loading...</div> : (
            <table>
              <thead>
                <tr>
                  <th>#</th><th>Device</th><th>OS</th><th>IP</th>
                  <th>Vulnerabilities</th><th>Critical</th><th>High</th>
                  <th style={{ minWidth: 160 }}>Total Risk Score</th>
                </tr>
              </thead>
              <tbody>
                {(data?.topRiskDevices || []).length === 0 && (
                  <tr><td colSpan={8} className="empty">No risk data available.</td></tr>
                )}
                {(data?.topRiskDevices || []).map((d, i) => {
                  const critical = d.vulns.filter(v => v.severity === 'critical').length;
                  const high     = d.vulns.filter(v => v.severity === 'high').length;
                  return (
                    <tr key={i}>
                      <td style={{ fontSize: 13, fontWeight: 700, color: '#94a3b8' }}>#{i + 1}</td>
                      <td><strong>{d.hostname}</strong></td>
                      <td style={{ fontSize: 12, color: '#94a3b8' }}>{d.os}</td>
                      <td style={{ fontSize: 12, color: '#94a3b8' }}>{d.ipAddress}</td>
                      <td style={{ fontSize: 13, fontWeight: 600 }}>{d.vulns.length}</td>
                      <td>
                        {critical > 0 && (
                          <span className="badge critical">{critical}</span>
                        )}
                      </td>
                      <td>
                        {high > 0 && (
                          <span className="badge high">{high}</span>
                        )}
                      </td>
                      <td><RiskBar score={Math.min(100, d.totalRisk / 10)} /></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* ── EDIT RISK SCORE MODAL ── */}
      {editVuln && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: '#fff', borderRadius: 12, padding: 32, width: 480,
            boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: '#1e2a3b', marginBottom: 4 }}>
              Update Risk Score
            </div>
            <div style={{ fontSize: 12, color: '#94a3b8', marginBottom: 20 }}>
              {editVuln.title} · {editVuln.hostname}
            </div>

            <div className="form-group">
              <label className="form-label">CVSS Score (0.0 – 10.0)</label>
              <input className="form-input" type="number" min="0" max="10" step="0.1"
                placeholder="e.g. 9.8 for critical CVE"
                value={editForm.cvssScore}
                onChange={e => setEditForm(f => ({ ...f, cvssScore: e.target.value }))} />
              <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>
                Check NVD (nvd.nist.gov) for the official CVSS score
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Exploitability</label>
              <select className="form-input" value={editForm.exploitability}
                onChange={e => setEditForm(f => ({ ...f, exploitability: e.target.value }))}>
                <option value="high">High — exploit publicly available</option>
                <option value="medium">Medium — exploit exists but complex</option>
                <option value="low">Low — no known exploit</option>
                <option value="unknown">Unknown</option>
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">Business Impact</label>
              <select className="form-input" value={editForm.businessImpact}
                onChange={e => setEditForm(f => ({ ...f, businessImpact: e.target.value }))}>
                <option value="critical">Critical — core business system</option>
                <option value="high">High — important system</option>
                <option value="medium">Medium — standard workstation</option>
                <option value="low">Low — non-critical device</option>
              </select>
            </div>

            <div className="flex-gap" style={{ justifyContent: 'flex-end', marginTop: 20 }}>
              <button className="btn btn-ghost" onClick={() => setEditVuln(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={saveRisk} disabled={saving}>
                {saving ? '⟳ Saving...' : 'Update Risk Score'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
